<template>
  <div class="bridge-signing-table">
    <v-card-title class="toolbar primary white--text">
      <div>
        <span class="headline">
          <h1 class="headline ml-2">
            <v-icon dark>description</v-icon>
            Paybacks - {{ item.description }}
          </h1>
        </span>
        <div>
          SPV Wallet ID: {{ item.source && item.source.spvWalletId ? item.source.spvWalletId : 'Not available' }}
          <span v-if="hasWalletBalance"> ⸺ Balance: {{ walletBalance }}</span>
        </div>
        <div>
          Total Net Amount: {{ centsToEuros(item.totalNetAmount ? item.totalNetAmount : 0) }}
          <span v-if="item.totalNetAmounts">
            ⸺ DDP: {{ centsToEuros(item.totalNetAmounts.ddp ? item.totalNetAmounts.ddp : 0) }}
            ⸺ SEPA: {{ centsToEuros(item.totalNetAmounts.sepa ? item.totalNetAmounts.sepa : 0) }}
          </span>
        </div>
      </div>
      <v-spacer />
      <v-text-field
        v-model="search"
        append-icon="search"
        label="Filter results"
        single-line
        flat
        dark
        color="primary"
        solo-inverted
        hide-details
      />
      <v-btn
        dark
        @click="backToList"
      >
        back to list
      </v-btn>

      <template
        v-if="showSelection"
      >
        <v-tooltip
          top
          dark
        >
          <template v-slot:activator="{ on }">
            <v-btn
              color="secondary"
              fab
              dark
              absolute
              bottom
              right
              @click.stop="onCancel"
              v-on="on"
            >
              <v-icon>done_all</v-icon>
            </v-btn>
          </template>
          <span>Cancel</span>
        </v-tooltip>
        <v-tooltip
          top
          dark
        >
          <template v-slot:activator="{ on }">
            <v-btn
              class="right-margin1"
              color="secondary"
              fab
              dark
              absolute
              bottom
              right
              @click.stop="onTogglePaymentProvider"
              v-on="on"
            >
              <v-icon>published_with_changes</v-icon>
            </v-btn>
          </template>
          <span>Toggle Selection</span>
        </v-tooltip>

        <v-tooltip
          v-if="showType === 'paymentProvider'"
          top
          dark
        >
          <template v-slot:activator="{ on }">
            <v-btn
              v-if="selectedHolderIds.length > 0"
              class="right-margin2"
              color="secondary"
              fab
              dark
              absolute
              bottom
              right
              @click.stop="onPreparePaymentProvider"
              v-on="on"
            >
              <v-icon>send</v-icon>
            </v-btn>
          </template>
          <span>Submit Payment Provider Changes</span>
        </v-tooltip>

        <v-tooltip
          v-if="showType === 'rollback'"
          top
          dark
        >
          <template v-slot:activator="{ on }">
            <v-btn
              v-if="selectedHolderIds.length > 0"
              class="right-margin2"
              color="secondary"
              fab
              dark
              absolute
              bottom
              right
              @click.stop="onPrepareRollback"
              v-on="on"
            >
              <v-icon>send</v-icon>
            </v-btn>
          </template>
          <span>Submit Rollback</span>
        </v-tooltip>
      </template>
      <template
        v-else
      >
        <v-tooltip
          top
          dark
        >
          <template v-slot:activator="{ on }">
            <v-btn
              color="secondary"
              fab
              dark
              absolute
              bottom
              right
              :disabled="isApproveDisabled()"
              @click.stop="showApproveDialog"
              v-on="on"
            >
              <v-icon>done_all</v-icon>
            </v-btn>
          </template>
          <span>Approve</span>
        </v-tooltip>

        <v-tooltip
          top
          dark
        >
          <template v-slot:activator="{ on }">
            <v-btn
              class="right-margin1"
              color="secondary"
              fab
              dark
              absolute
              bottom
              right
              :disabled="isConfirmDisabled()"
              @click.stop="showConfirmDialog"
              v-on="on"
            >
              <v-icon>done</v-icon>
            </v-btn>
          </template>
          <span>Confirm</span>
        </v-tooltip>

        <v-tooltip
          top
          dark
        >
          <template v-slot:activator="{ on }">
            <v-btn
              class="right-margin2"
              color="secondary"
              fab
              dark
              absolute
              bottom
              right
              @click.stop="onDebugPayout"
              v-on="on"
            >
              <v-icon>adb</v-icon>
            </v-btn>
          </template>
          <span>Debug</span>
        </v-tooltip>

        <v-tooltip
          top
          dark
        >
          <template v-slot:activator="{ on }">
            <v-btn
              class="right-margin3"
              color="secondary"
              fab
              dark
              absolute
              bottom
              right
              @click.stop="() => onChangeSelection('paymentProvider')"
              v-on="on"
            >
              <v-icon>payments</v-icon>
            </v-btn>
          </template>
          <span>Change Payment Provider</span>
        </v-tooltip>

        <v-tooltip
          top
          dark
          v-if="canRollback()"
        >
          <template v-slot:activator="{ on }">
            <v-btn
              class="right-margin4"
              color="secondary"
              fab
              dark
              absolute
              bottom
              right
              @click.stop="() => onChangeSelection('rollback')"
              v-on="on"
            >
              <v-icon>undo</v-icon>
            </v-btn>
          </template>
          <span>Rollback Paybacks</span>
        </v-tooltip>
      </template>

      <v-tooltip
        top
        dark
      >
        <template v-slot:activator="{ on }">
          <v-btn
            color="secondary"
            fab
            dark
            absolute
            bottom
            left
            @click.stop="onRefresh"
            v-on="on"
          >
            <v-icon>refresh</v-icon>
          </v-btn>
        </template>
        <span>Refresh</span>
      </v-tooltip>
    </v-card-title>

    <v-data-table
      :headers="headers"
      :items="filteredItems"
      :pagination.sync="pagination"
      :loading="isLoading"
      loading-text="Loading..."
      disable-initial-sort
    >
      <template
        slot="items"
        slot-scope="props"
      >
        <tr>
          <td>
            <UserChip
              :id="parseInt(props.item.userId, 10)"
            />
          </td>
          <td class="text-center">
            {{ paymentProviders()[props.item.paymentProvider] }}
          </td>
          <td class="text-center">
            {{ centsToEuros(props.item.netAmount) }}
          </td>
          <td class="text-center">
            <PaybackWalletChip
              :id="getTargetWallet(props.item)"
              :type="props.item.paymentProvider"
            />
          </td>
          <td class="text-center">
            {{ props.item.status }}
          </td>
          <td class="text-center">
            {{ cleanEmail(props.item.confirmedBy || '') }}
          </td>
          <td class="text-center">
            {{ cleanEmail(props.item.approvedBy || '') }}
          </td>
          <td class="text-center">
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  dark
                  flat
                  icon
                  color="secondary"
                  v-on="on"
                  @click.stop="() => onDebugPayback(props.item)"
                >
                  <v-icon>adb</v-icon>
                </v-btn>
              </template>
              <span>Debug</span>
            </v-tooltip>
            <template
              v-if="showSelection"
            >
              <v-checkbox
                :key="props.item.holderId"
                v-model="selectedHolderIds"
                :value="props.item.holderId"
                style="display: inline-block; position: relative; top: 9px;"
              />
            </template>
          </td>
        </tr>
      </template>
    </v-data-table>

    <DebugDialog
      :visible="isDebugDialogActive"
      :debug-object="debugObject"
      :title="debugTitle"
      @close="isDebugDialogActive = false"
    />

    <PaybackConfirmDialog
      key="confirm_dialog"
      :visible="isConfirmDialogActive()"
      :payout="item"
      @close="hideConfirmDialog()"
      @submit="onConfirm()"
    />

    <PaybackApproveDialog
      key="approve_dialog"
      :visible="isApproveDialogActive()"
      :payout="item"
      @close="hideApproveDialog()"
      @submit="onApprove()"
    />

    <ChangePaymentProviderDialog
      key="change_payment_provider_dialog"
      :visible="isPaymentProviderDialogActive"
      :payout-id="payoutId"
      :holder-ids="selectedHolderIds"
      @close="hideConfirmPaymentProviderDialog"
    />

    <RollbackPaybackDialog
      key="rollback_dialog"
      :visible="isRollbackDialogActive"
      :payout-id="payoutId"
      :holder-ids="selectedHolderIds"
      @close="hideConfirmRollbackDialog"
    />

    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      :timeout="snackbarTimeout"
    >
      {{ snackBarText }}
      <v-btn
        dark
        flat
        @click="snackbar = false"
      >
        Close
      </v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import DebugDialog from '@/components/payoutsAndTaxes/payout/dialogs/shared/DebugDialog.vue';
import PaybackConfirmDialog from '@/components/payoutsAndTaxes/payout/dialogs/payback/PaybackConfirmDialog.vue';
import PaybackApproveDialog from '@/components/payoutsAndTaxes/payout/dialogs/payback/PaybackApproveDialog.vue';
import UserChip from '@/components/shared/user/UserChip.vue';
import { centsToEuros } from '@/utils/numberFormats';
import ChangePaymentProviderDialog from '@/components/payoutsAndTaxes/payout/dialogs/payback/ChangePaymentProviderDialog.vue';
import PaybackWalletChip from '@/components/payoutsAndTaxes/payout/shared/PaybackWalletChip.vue';
import RollbackPaybackDialog from '@/components/payoutsAndTaxes/payout/dialogs/payback/RollbackPaybackDialog.vue';

export default {
  name: 'PaybackView',
  components: {
    RollbackPaybackDialog,
    PaybackWalletChip,
    ChangePaymentProviderDialog,
    UserChip,
    PaybackApproveDialog,
    PaybackConfirmDialog,
    DebugDialog,
  },
  data() {
    return {
      selectedHolderIds: [],
      centsToEuros,
      payoutId: '',
      confirmDialog: false,
      approveDialog: false,
      showType: '',
      showSelection: false,
      isPaymentProviderDialogActive: false,
      isRollbackDialogActive: false,
      debugObject: {},
      debugTitle: '',
      isDebugDialogActive: false,
      isLoading: false,
      pagination: {
        rowsPerPage: 25,
      },
      search: '',
      headers: [
        { text: 'User ID', value: 'userId', align: 'left' },
        { text: 'Payment Provider', value: 'paymentProvider', align: 'center' },
        { text: 'Net Amount', value: 'netAmount', align: 'center' },
        { text: 'Target Wallet', value: 'targetWallet', align: 'center' },
        { text: 'Status', value: 'status', align: 'center' },
        { text: 'Confirmed', value: 'confirmedBy', align: 'center' },
        { text: 'Approved', value: 'approvedBy', align: 'center' },

        { text: 'Actions', sortable: false, align: 'center' },
      ],
      snackbar: false,
      snackbarTimeout: 5000,
      snackbarColor: 'primary',
      snackBarText: '',
    };
  },
  computed: {
    item() {
      return this.$store.getters.getPaybackView;
    },
    list() {
      return this.item.paybacks || [];
    },
    walletInfo() {
      return this.$store.getters.getPaybackWalletInfo;
    },
    hasWalletBalance() {
      return !!(this.walletInfo && this.walletInfo.wallet && this.walletInfo.wallet.balance);
    },
    walletBalance() {
      const formatter = new Intl.NumberFormat(
        'de-DE',
        {
          style: 'currency',
          currency: this.walletInfo.wallet.balance.currency,
        },
      );

      return formatter.format(this.walletInfo.wallet.balance.amount / 100);
    },
    filteredItems() {
      const { search } = this;

      return search.length < 1 ?
        this.list :
        this.list.filter((item) => {
          let found = false;
          Object.keys(item)
            .map((key) => {
              if (item[key] && String(item[key]).toLowerCase().indexOf(search.toLowerCase()) > -1) {
                found = true;
              }

              return key;
            });

          return found;
        });
    },
  },
  mounted() {
    this.onRefresh();
  },
  methods: {
    hideConfirmPaymentProviderDialog() {
      this.isPaymentProviderDialogActive = false;
      this.$forceUpdate();
    },
    hideConfirmRollbackDialog() {
      this.isRollbackDialogActive = false;
      this.$forceUpdate();
    },
    paymentProviders() {
      const data = this.$store.getters.getPaybackTypes;
      if (!data || !data.payback || !data.payback.paymentProvider) {
        return {};
      }

      return data.payback.paymentProvider;
    },
    onRefresh() {
      this.$store.dispatch('fetchPatPaybackTypes');
      this.$store.dispatch('fetchPatPaybackView', this.$route.params.payoutId).then(() => {
        if (this.item && this.item.source && this.item.source.spvWalletId) {
          this.$store.dispatch('fetchPatPaybackWalletInfo', this.item.source.spvWalletId).catch((error) => {
            this.snackbarColor = 'red';
            this.snackBarText = `Failed to load wallet info: ${error.message}`;
            this.snackbar = true;
          });
        }
      }).catch((error) => {
        this.snackbarColor = 'red';
        this.snackBarText = `Failed to load list: ${error.message}`;
        this.snackbar = true;
      });
    },
    getTargetWallet(row) {
      if (row.paymentProvider === 'ddp') {
        return row.ddaId;
      }

      if (row.paymentProvider === 'sepa') {
        const pi = row.paymentInformation;

        return pi && pi.sepa_iban ? row.paymentInformation.sepa_iban : 'No Sepa IBAN available';
      }

      return 'Payment Provider not implemented.';
    },
    cleanEmail(email) {
      return email.split('@')[0];
    },
    async backToList() {
      await this.$router.push(
        '/pat/paybacks',
      );
    },
    onCancel() {
      this.showType = '';
      this.showSelection = false;
      this.selectedHolderIds = [];
    },
    onChangeSelection(type) {
      this.showType = type;
      this.showSelection = !this.showSelection;

      if (this.showSelection) {
        this.checkAllHolderIds();
      }
    },
    onTogglePaymentProvider() {
      if (this.selectedHolderIds.length === 0) {
        this.checkAllHolderIds();
      } else {
        this.uncheckAllHolderIds();
      }
    },
    onPreparePaymentProvider() {
      this.payoutId = this.$route.params.payoutId;
      this.isPaymentProviderDialogActive = true;
    },
    onPrepareRollback() {
      this.payoutId = this.$route.params.payoutId;
      this.isRollbackDialogActive = true;
    },
    checkAllHolderIds() {
      this.uncheckAllHolderIds();
      this.list.forEach((payback) => {
        this.selectedHolderIds.push(payback.holderId);
      });
    },
    uncheckAllHolderIds() {
      this.selectedHolderIds = [];
    },
    onDebugPayout() {
      this.debugObject = this.list;
      this.debugTitle = 'Debug Payout';
      this.isDebugDialogActive = true;
    },
    onDebugPayback(item) {
      this.debugObject = item;
      this.debugTitle = 'Debug Payback';
      this.isDebugDialogActive = true;
    },
    isConfirmEnabled() {
      let isEnabled = false;

      this.list.forEach((payback) => {
        if (!payback.confirmedBy) {
          isEnabled = true;
        }
      });

      return isEnabled;
    },
    isConfirmDisabled() {
      return !this.isConfirmEnabled();
    },
    showConfirmDialog() {
      this.confirmDialog = true;
      this.$forceUpdate();
    },
    isConfirmDialogActive() {
      return this.confirmDialog === true;
    },
    hideConfirmDialog() {
      this.confirmDialog = false;
      this.$forceUpdate();
    },
    onConfirm() {
      this.$store.dispatch('confirmPatPayoutPayback', this.$route.params.payoutId).then(() => {
        this.snackbarColor = 'primary';
        this.snackBarText = 'Successfully approved.';
        this.snackbar = true;
      }).catch((error) => {
        this.snackbarColor = 'red';
        this.snackBarText = `Confirm error: ${error.message}`;
        this.snackbar = true;
      });
    },
    isApproveEnabled() {
      let isEnabled = true;
      let isAllApproved = true;

      this.list.forEach((payback) => {
        if (!payback.confirmedBy) {
          isEnabled = false;
        }

        if (!payback.approvedBy) {
          isAllApproved = false;
        }
      });

      return isEnabled && !isAllApproved;
    },
    isApproveDisabled() {
      return !this.isApproveEnabled();
    },
    showApproveDialog() {
      this.approveDialog = true;
      this.$forceUpdate();
    },
    isApproveDialogActive() {
      return this.approveDialog === true;
    },
    hideApproveDialog() {
      this.approveDialog = false;
      this.$forceUpdate();
    },
    onApprove() {
      this.$store.dispatch('approvePatPayoutPayback', this.$route.params.payoutId).then(() => {
        this.snackbarColor = 'primary';
        this.snackBarText = 'Successfully approved.';
        this.snackbar = true;
      }).catch((error) => {
        this.snackbarColor = 'red';
        this.snackBarText = `Approve error: ${error.message}`;
        this.snackbar = true;
      });
    },
    canRollback() {
      let can = false;

      this.list.forEach((payback) => {
        if (payback.status === 'succeeded' && payback.paymentProvider === 'ddp') {
          can = true;
        }
      });

      return can;
    },
  },
};
</script>
<style lang="scss">
.bridge-signing-table {
  > .toolbar {
    position: relative;
    padding: 24px 24px 32px 24px;
    margin-bottom: 32px;
  }
}

.text-center {
  text-align: center;
}

.mass-create {
  margin-right: 70px;
}

.right-margin1 {
  margin-right: 70px;
}
.right-margin2 {
  margin-right: 140px;
}
.right-margin3 {
  margin-right: 210px;
}
.right-margin4 {
  margin-right: 280px;
}
.right-margin5 {
  margin-right: 350px;
}
</style>



// WEBPACK FOOTER //
// src/components/payoutsAndTaxes/payout/PaybackView.vue